const GIFT_PROPERTY = '_gwp'
const CAMPAIGN_PROPERTY = '_gwp_campaign'

/**
 * Orchestrates the gift line and the automatic discount code as the cart
 * changes. Threshold evaluation is done server-side (see the handler snippet);
 * this reads the emitted config and reacts to it.
 */
class GiftWithPurchase {
  constructor () {
    this.state = null
    this.onCartRender = this.onCartRender.bind(this)
    this.onGiftReevaluate = this.onGiftReevaluate.bind(this)
    this.onClick = this.onClick.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_RENDER,
      this.onCartRender,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.GWP_REEVALUATE,
      this.onGiftReevaluate,
    )

    // Drawer markup is replaced on every cart render, so delegate from the
    // document rather than binding the buttons directly.
    document.addEventListener('click', this.onClick)

    this.init()
  }

  onClick (event) {
    const addButton = event.target.closest('[data-gwp-add]')
    if (addButton) {
      event.preventDefault()
      this.claimGift(addButton)
      return
    }

    const editButton = event.target.closest('[data-gwp-edit]')
    if (editButton) {
      event.preventDefault()
      this.editGift(editButton)
      return
    }

    const tierTab = event.target.closest('[data-gwp-tier-select]')
    if (tierTab) {
      event.preventDefault()
      this.selectTier(tierTab.dataset.gwpTierSelect)
      return
    }

    // Opens the detail drawer (natively, via data-drawers-sub-nav) to a specific
    // tier and, from the selector's Details link, a specific product. No
    // preventDefault so the native drawer trigger still fires. Each surface has
    // its own key, so match on the shared prefix.
    const detailTrigger = event.target.closest(
      '[data-drawers-trigger^="gift-with-purchase-detail"]',
    )
    if (detailTrigger) {
      this.selectTier(
        detailTrigger.dataset.gwpTier,
        detailTrigger.dataset.gwpGiftHandle,
      )
    }
  }

  /**
   * Shows the chosen tier's carousel in the detail drawer, hides the others and
   * optionally moves the carousel to a given product. A hidden carousel is
   * measured by keen-slider at zero width, so re-measure it once visible.
   *
   * The push cart renders on every page, so /cart holds a second copy of the
   * drawer alongside the one its own section emits. They open under separate
   * keys, but both describe the same campaign, so drive every copy rather than
   * depending on which one the controller happened to open.
   *
   * @param {string} tierId
   * @param {string} [handle] gift handle to jump to
   */
  selectTier (tierId, handle) {
    if (!tierId) return

    for (const drawer of document.querySelectorAll(
      'gift-with-purchase-detail',
    )) {
      let activePanel = null
      drawer.querySelectorAll('[data-gwp-tier-panel]').forEach(panel => {
        const active = panel.dataset.gwpTierPanel === tierId
        panel.hidden = !active
        if (active) activePanel = panel
      })

      drawer.querySelectorAll('.gwp-Tab').forEach(tab => {
        tab.classList.toggle('gwp-Tab-active', tab.dataset.gwpTier === tierId)
      })

      const carousel = activePanel?.querySelector('gift-with-purchase-carousel')
      if (!carousel?.carousel) continue

      const panel = activePanel
      requestAnimationFrame(() => {
        carousel.carousel.update()

        if (!handle) return

        const slides = [...panel.querySelectorAll('[data-gwp-detail-product]')]
        const index = slides.findIndex(
          slide => slide.dataset.gwpDetailProduct === handle,
        )
        if (index >= 0) carousel.carousel.moveToIdx(index, true)
      })
    }
  }

  /**
   * The detail drawer is re-emitted whenever its cart section re-renders and each
   * copy moves itself into <site-drawers>. Keep only the newest so duplicates do
   * not pile up in the DOM or the drawer controller registry.
   *
   * Deduping is per key. The push cart and /cart each own a drawer under their
   * own key, and both are live at once on the cart page, so collapsing across
   * keys would delete a drawer another section still owns.
   */
  syncDetailDrawer () {
    const controller = window.drawers
    if (!controller) return

    const all = [
      ...document.querySelectorAll(
        'site-drawer[key^="gift-with-purchase-detail"]',
      ),
    ]
    const keys = new Set(all.map(drawer => drawer.getAttribute('key')))

    for (const key of keys) {
      const drawers = all.filter(drawer => drawer.getAttribute('key') === key)
      if (drawers.length < 2) continue

      // The newest copy still sits in the cart section awaiting its move; the
      // older copies have already moved into <site-drawers>. Drop the old ones.
      const fresh = drawers.find(drawer => drawer.parentElement !== controller)
      const keep = fresh ?? drawers[drawers.length - 1]

      for (const drawer of drawers) {
        if (drawer !== keep) drawer.remove()
      }

      controller.drawers = controller.drawers.filter(
        drawer => drawer.key !== key || drawer === keep,
      )
    }
  }

  /**
   * Removes the claimed gift so the selector reopens.
   *
   * @param {HTMLElement} button
   */
  async editGift (button) {
    if (button.disabled) return

    const key = button.dataset.gwpEdit
    if (!key) return

    button.disabled = true

    try {
      const state = await window.BAO.Cart.removeItem(key, {
        sections: this.sectionNames,
      })
      this.renderSections(state?.sections)
    } catch (error) {
      console.error('Gift with purchase edit error:', error)
      button.disabled = false
    }
  }

  /**
   * True when running on the full cart page rather than the push cart drawer.
   *
   * @returns {boolean}
   */
  get onCartPage () {
    return document.querySelector('cart-items') != null
  }

  /**
   * The section list for the surface we are on. Cart mutations and re-renders
   * target the cart page's template-cart-items section on /cart, and the push
   * cart sections in the drawer.
   *
   * @returns {Object[]}
   */
  get sections () {
    return this.onCartPage
      ? window.BAO.sectionRenderer.cartSections
      : window.BAO.sectionRenderer.pushCartSections
  }

  /**
   * @returns {string[]}
   */
  get sectionNames () {
    return this.sections.map(section => section.section)
  }

  /**
   * Swaps the cart section markup returned by a cart mutation. removeItem does
   * not re-render the current tab on its own, so the caller must do it.
   *
   * @param {Object} stateSections
   */
  renderSections (stateSections) {
    if (!stateSections) return

    const sections = this.sections
      .filter(section => section.section in stateSections)
      .map(section =>
        Object.assign({}, section, { html: stateSections[section.section] }),
      )

    if (!sections.length) return

    window.BAO.sectionRenderer.renderMultiple(sections)
    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_RENDER)
  }

  /**
   * The PDP tier block is server-rendered inside the product section, so it does
   * not follow cart changes on its own. Re-fetch it in isolation and swap it in.
   * A no-op anywhere the block is absent.
   */
  async renderPdp () {
    const current = document.querySelector('[data-gwp-pdp]')
    if (!current) return

    try {
      const html = await window.BAO.sectionRenderer.fetchSingle(
        'gift-with-purchase-pdp',
      )
      const fresh = window.BAO.sectionRenderer
        .convertToDOM(html)
        .querySelector('[data-gwp-pdp]')

      if (fresh) current.replaceWith(fresh)
    } catch (error) {
      console.error('Gift with purchase PDP render error:', error)
    }
  }

  /**
   * Adds the chosen gift as a marked line item. One gift per campaign, so any
   * existing gift is cleared first.
   *
   * @param {HTMLElement} button
   */
  async claimGift (button) {
    if (button.disabled) return

    const gift = button.closest('[data-gwp-gift]')
    const select = gift?.querySelector('[data-gwp-variant-select]')
    const variantId = select ? select.value : button.dataset.gwpVariantId
    const quantity = parseInt(button.dataset.gwpQuantity || '1', 10)

    if (!variantId) return

    const config = this.getConfig()
    const campaignId = config?.campaignId ?? null

    button.disabled = true

    try {
      this.state = await window.BAO.Cart.cartGet()
      await this.removeGifts(campaignId)

      await window.BAO.Cart.addItem(
        {
          id: variantId,
          quantity,
          properties: {
            [GIFT_PROPERTY]: 'true',
            [CAMPAIGN_PROPERTY]: campaignId,
          },
        },
        { sections: this.sectionNames },
      )
    } catch (error) {
      console.error('Gift with purchase claim error:', error)
      button.disabled = false
    }
  }

  async init () {
    try {
      this.syncDetailDrawer()
      this.state = await window.BAO.Cart.cartGet()
      await this.removeOrphanedGifts()
    } catch (error) {
      console.error('Gift with purchase init error:', error)
    }
  }

  async onCartRender (event) {
    this.state = event.detail?.state ?? (await window.BAO.Cart.cartGet())
    this.syncDetailDrawer()

    const config = this.getConfig()
    if (!config) return

    await this.removeOrphanedGifts()

    // Manual claim + auto-removal only; the gift is never added automatically.
    // Drop any claimed gift whose product is no longer in the unlocked set.
    // Falling from Tier 2 to Tier 1 still leaves a tier qualified, so a
    // campaign-wide "any tier qualifies" check would wrongly keep the
    // now-unearned Tier 2 gift.
    const state = await this.removeUnearnedGifts(config)
    if (state) this.renderSections(state.sections)

    await this.renderPdp()
  }

  /**
   * Re-evaluates against fresh config while push-cart-items is withheld from the
   * DOM (the wishlist prompt on item removal). Removes a now-unearned gift and
   * strips just its line node, leaving the rest of the section, so the wishlist
   * prompt survives. The threshold message is not refreshed here; it updates
   * when the prompt is dismissed and the section re-renders.
   *
   * @param {CustomEvent} event
   */
  async onGiftReevaluate (event) {
    const html = event.detail?.pushCartItemsHtml
    if (!html) return

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const config = this.parseConfigFrom(doc)
    if (!config) return

    this.state = await window.BAO.Cart.cartGet()

    // Drop the gift line's DOM node directly instead of re-rendering
    // push-cart-items, which would take the wishlist prompt with it. The line
    // key from /cart.js does not match the Liquid-rendered data-key (the
    // property hashes differ), so target the gift line by its edit control and
    // the stable variant id rather than the full key.
    for (const gift of this.unearnedGifts(config)) {
      await window.BAO.Cart.removeItem(gift.key, { sections: [] })

      const variantId = gift.key.split(':')[0]
      document.querySelectorAll('cart-item').forEach(node => {
        const isGiftLine = node.querySelector('[data-gwp-edit]')
        const sameVariant = (node.dataset.key || '').startsWith(`${variantId}:`)
        if (isGiftLine && sameVariant) node.remove()
      })
    }
  }

  /**
   * @returns {Object|null}
   */
  getConfig () {
    return this.parseConfigFrom(document)
  }

  /**
   * @param {Document|Element} root
   * @returns {Object|null}
   */
  parseConfigFrom (root) {
    const element = root.querySelector('[data-gift-with-purchase-config]')
    if (!element) return null

    try {
      return JSON.parse(element.textContent.trim())
    } catch (error) {
      console.error('Gift with purchase: invalid config', error)
      return null
    }
  }

  /**
   * Claimed gifts whose product is no longer offered by any qualified tier
   * (i.e. no longer in the unlocked union).
   *
   * @param {Object} config
   * @returns {Object[]}
   */
  unearnedGifts (config) {
    const unlockedHandles = new Set(
      (config.tiers || [])
        .filter(tier => tier.qualified)
        .flatMap(tier => (tier.gifts || []).map(gift => gift.handle)),
    )

    return (this.state?.items || []).filter(
      item =>
        item.properties?.[GIFT_PROPERTY] === 'true' &&
        item.properties?.[CAMPAIGN_PROPERTY] === config.campaignId &&
        !unlockedHandles.has(item.handle),
    )
  }

  /**
   * Removes claimed gifts whose product is not offered by any currently
   * qualified tier. Covers falling below the tier a gift was claimed at while a
   * lower tier still qualifies.
   *
   * @param {Object} config
   * @returns {Promise<Object|null>} the last cart state, or null if nothing removed
   */
  async removeUnearnedGifts (config) {
    let state = null
    for (const gift of this.unearnedGifts(config)) {
      state = await window.BAO.Cart.removeItem(gift.key, {
        sections: this.sectionNames,
      })
    }

    return state
  }

  /**
   * Removes claimed gifts for the given campaign.
   *
   * @param {string} campaignId
   * @returns {Promise<Object|null>} the last cart state, or null if nothing removed
   */
  async removeGifts (campaignId) {
    const gifts = (this.state?.items || []).filter(
      item =>
        item.properties?.[GIFT_PROPERTY] === 'true' &&
        item.properties?.[CAMPAIGN_PROPERTY] === campaignId,
    )

    let state = null
    for (const gift of gifts) {
      state = await window.BAO.Cart.removeItem(gift.key, {
        sections: this.sectionNames,
      })
    }

    return state
  }

  /**
   * Removes gifts left over from campaigns that are no longer active.
   */
  async removeOrphanedGifts () {
    const config = this.getConfig()
    const activeCampaignId = config?.campaignId ?? null

    const orphaned = (this.state?.items || []).filter(
      item =>
        item.properties?.[GIFT_PROPERTY] === 'true' &&
        item.properties?.[CAMPAIGN_PROPERTY] !== activeCampaignId,
    )

    for (const item of orphaned) {
      await window.BAO.Cart.removeItem(item.key, {
        sections: this.sectionNames,
      })
    }
  }
}

window.BAO.GiftWithPurchase = new GiftWithPurchase()
